import { Link } from 'react-router-dom';
import {
  Users, HeartHandshake, Mic, ShieldCheck, Heart, Building2,
  HandHeart, Home as HomeIcon, Mail, Phone, MapPin, ArrowRight, Lock
} from 'lucide-react';
import SEO from '@/components/SEO';
import './Foundation.css';

const EMAIL = 'helloelderra@gmail.com';
const PHONE = '8920455730';

const HERO_PHOTO = '/images/foundation/p03.jpg';

const PHOTOS = [
  { src: '/images/foundation/p02.jpg', alt: 'Seniors gathered together after a meetup' },
  { src: '/images/foundation/p01.jpg', alt: 'A hands-on digital literacy session with seniors' },
  { src: '/images/foundation/p07.jpg', alt: 'Seniors attending an Elderra Foundation workshop' },
  { src: '/images/foundation/p14.jpg', alt: 'Seniors spelling out Elderra at a community meetup' },
  { src: '/images/foundation/p13.jpg', alt: 'A trainer teaching seniors at a meetup' },
  { src: '/images/foundation/p04.jpg', alt: 'A senior holding a sign: I am 75 and I am a YouTuber' }
];

const WORK = [
  {
    icon: Users,
    title: 'Senior Meetups',
    text: 'Friendly, hands-on sessions where older people learn to use everyday apps, spot scams and phishing, and know what to do if they are cheated. After each meetup, participants join a WhatsApp group for their city, where they stay in touch, help each other and make new friends. When we can, we also celebrate Diwali, Onam and New Year together.'
  },
  {
    icon: HeartHandshake,
    title: 'DigiMitra',
    text: 'A human guide who is always just a call away. DigiMitras help seniors take part in digital life in ways that matter to them, from launching their own YouTube channels and writing memoirs to becoming changemakers in their neighbourhoods.'
  },
  {
    icon: Mic,
    title: 'Voice Companion',
    text: 'Seniors simply send a WhatsApp voice note, in 12 Indian languages or English, to renew their pension, apply for government schemes, pay bills, and order food, groceries, medicines and travel. There are no apps to learn and no forms to fill. With the senior\u2019s consent, family members can keep an eye on things through a companion app.'
  },
  {
    icon: ShieldCheck,
    title: 'A safer way to pay',
    text: 'Seniors pay through a wallet made only for them, which they or their families can top up. Because payments come from a wallet with a limited balance instead of a bank account linked to UPI, their savings are far less exposed to online fraud.'
  }
];

const STATS = [
  { num: '4,000', label: 'older people have attended our meetups in six cities' },
  { num: '12,000', label: 'people have used the Voice Companion, in 12 Indian languages and English' },
  { num: '7', label: 'states where our community managers run local senior communities' }
];

const SUPPORT = [
  {
    icon: Building2,
    title: 'CSR partners',
    text: 'Companies can fund meetups, DigiMitra support, community gatherings and the expansion of our work to underserved older people. Giant-Tree Foundation is registered for CSR (CSR-1: CSR00107571).',
    cta: 'Partner with us',
    href: `mailto:${EMAIL}?subject=CSR%20partnership%20with%20Elderra%20Foundation`,
    testid: 'foundation-csr-cta'
  },
  {
    icon: Heart,
    title: 'Donate',
    text: 'Your donation helps an older person learn, stay safe online and stay connected. Donations are eligible for tax deduction under Section 80G (provisional approval). We currently accept donations only from Indian citizens and Indian sources.',
    cta: 'Donate',
    to: '/donate',
    testid: 'foundation-donate-cta'
  },
  {
    icon: HandHeart,
    title: 'Volunteer',
    text: 'Become a DigiMitra, help at a meetup in your city, or teach a skill you love. A little of your time can open up the digital world for someone who raised a generation.',
    cta: 'Volunteer with us',
    to: '/digimitra',
    testid: 'foundation-volunteer-cta'
  },
  {
    icon: HomeIcon,
    title: 'Community partners',
    text: 'NGOs, self-help groups, resident welfare associations and Panchayats can host meetups and help us reach elders in their area.',
    cta: 'Host a meetup',
    href: `mailto:${EMAIL}?subject=Host%20an%20Elderra%20Foundation%20meetup`,
    testid: 'foundation-host-cta'
  }
];

const SAFEGUARDS = [
  ['Consent first.', 'Family members can see a senior\u2019s activity only with the senior\u2019s permission.'],
  ['Your story, your choice.', 'We share photos, videos and stories of seniors only with their agreement.'],
  ['Safer payments.', 'Seniors pay from a wallet with a limited balance, not a linked bank account.'],
  ['Safe groups.', 'Every city WhatsApp group is admin-only for joining and is moderated by our community manager. Unknown numbers are verified before they are added, promotional and investment messages are removed, and any member who attempts a scam is blocked and reported.'],
  ['Data protection.', 'We collect only what is needed to deliver a service, store it securely in India, and never sell or rent personal data. In line with the Digital Personal Data Protection Act, 2023, a senior or their nominee can ask us at any time to show, correct or delete their data, or withdraw consent, by writing to us.']
];

const REGISTRATION = [
  ['Legal name', 'Giant-Tree Foundation'],
  ['Section 8 Licence No.', '179472, dated 31 January 2026'],
  ['CIN', 'U88900DL2026NPL462782'],
  ['PAN', 'AAMCG7420M'],
  ['12A (provisional)', 'URN AAMCG7420ME20251, valid AY 2026\u201327 to AY 2028\u201329'],
  ['80G (provisional)', 'URN AAMCG7420MF20261, valid AY 2026\u201327 to AY 2028\u201329'],
  ['CSR-1', 'CSR00107571'],
  ['Registered address', 'H. No. 234, Third Floor, Right Side Mustafa Loge, Jamia Nagar, New Delhi 110025']
];

const Foundation = () => {
  return (
    <div className="fdn-page" data-testid="foundation-page">
      <SEO
        title="Elderra Foundation | Digital independence for every elder"
        description="Elderra Foundation, an initiative of Giant-Tree Foundation, brings technology, training and community to older people across India who cannot afford paid digital support. Section 8 non-profit, 80G and CSR-1 registered."
        keywords="Elderra Foundation, Giant-Tree Foundation, Section 8 non profit seniors India, senior digital literacy NGO, 80G donation elderly, CSR partner senior citizens, DigiMitra volunteer"
        ogUrl="https://elderra.io/foundation"
      />

      {/* 1. Opening */}
      <header className="fdn-hero">
        <div className="fdn-wrap">
          <div className="fdn-hero-grid">
            <div>
              <span className="fdn-eyebrow">Elderra Foundation</span>
              <h1 data-testid="foundation-title">
                Digital independence for <em>every elder</em>, not only those who can afford it.
              </h1>
              <p>
                Elderra Foundation, an initiative of Giant-Tree Foundation, brings technology,
                training and community to older people across India who are being left behind
                by the digital world.
              </p>
              <div className="fdn-hero-actions">
                <a
                  className="fdn-btn fdn-btn-primary"
                  href={`mailto:${EMAIL}?subject=Partner%20with%20Elderra%20Foundation`}
                  data-testid="foundation-hero-partner-btn"
                >
                  Partner with us <ArrowRight size={18} />
                </a>
                <Link className="fdn-btn fdn-btn-ghost" to="/donate" data-testid="foundation-hero-donate-btn">
                  Donate
                </Link>
                <Link className="fdn-btn fdn-btn-ghost" to="/digimitra" data-testid="foundation-hero-volunteer-btn">
                  Volunteer
                </Link>
              </div>
            </div>
            <div className="fdn-hero-photo">
              <img src={HERO_PHOTO} alt="Seniors at an Elderra Foundation meetup" />
              <div className="fdn-photo-caption">
                From a senior meetup. Photos shared with participants&rsquo; permission.
              </div>
            </div>
          </div>
          <div className="fdn-parent-strip">
            An initiative of Giant-Tree Foundation &middot; Section 8 not-for-profit company &middot; CIN U88900DL2026NPL462782
          </div>
        </div>
      </header>

      {/* 2. Why we exist */}
      <section className="fdn-section" data-testid="foundation-why-section">
        <div className="fdn-wrap">
          <div className="fdn-why-grid">
            <div>
              <span className="fdn-eyebrow">Why we exist</span>
              <h2 className="fdn-h2">The elders who need help most can least afford it.</h2>
              <p className="fdn-pullquote">
                Elderra Foundation exists to close that gap.
              </p>
            </div>
            <div className="fdn-why-body">
              <p>
                Pensions, banking, medicines, government schemes and even staying in touch with
                family now happen on a phone. But most of these services were never designed for
                older people. As a result, many elders depend on others for everyday tasks, miss
                out on what they are owed, lose money to scams and grow isolated.
              </p>
              <p>
                Elderra, a technology startup recognised under Startup India and incubated at
                NSRCEL, IIM Bangalore, builds services that help seniors use the digital world on
                their own terms. But paid services mainly reach families in large cities. The
                elders who need help most, in smaller towns, villages and low-income households,
                are the ones who can least afford it.
              </p>
              <p>
                We take the same tools and support to older people who cannot pay, working with
                partners on a not-for-profit basis.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. What we do */}
      <section className="fdn-section" data-testid="foundation-work-section">
        <div className="fdn-wrap">
          <span className="fdn-eyebrow">What we do</span>
          <h2 className="fdn-h2">Four ways we show up for elders.</h2>
          <p className="fdn-lead">
            Technology on its own is not enough. Every programme pairs a tool with a person.
          </p>
          <div className="fdn-work-grid">
            {WORK.map(({ icon: Icon, title, text }) => (
              <article
                className="fdn-work-card"
                key={title}
                data-testid={`foundation-work-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="fdn-work-icon"><Icon size={26} /></div>
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Impact */}
      <section className="fdn-section fdn-impact" data-testid="foundation-impact-section">
        <div className="fdn-wrap">
          <span className="fdn-eyebrow">Our impact so far</span>
          <h2 className="fdn-h2">What has happened on the ground.</h2>
          <div className="fdn-impact-grid">
            {STATS.map(({ num, label }) => (
              <div key={num} data-testid={`foundation-stat-${num.replace(/,/g, '')}`}>
                <div className="fdn-stat-num">{num}</div>
                <div className="fdn-stat-label">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="fdn-section" data-testid="foundation-gallery-section">
        <div className="fdn-wrap">
          <span className="fdn-eyebrow">From our meetups</span>
          <h2 className="fdn-h2">Learning together, in person.</h2>
          <div className="fdn-gallery">
            {PHOTOS.map(({ src, alt }, i) => (
              <figure key={src} data-testid={`foundation-gallery-photo-${i}`}>
                <img src={src} alt={alt} loading="lazy" />
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* 9. Ways to support */}
      <section className="fdn-section" data-testid="foundation-support-section">
        <div className="fdn-wrap">
          <span className="fdn-eyebrow">Ways to support</span>
          <h2 className="fdn-h2">Help an elder step into the digital world.</h2>
          <div className="fdn-support-grid">
            {SUPPORT.map(({ icon: Icon, title, text, cta, href, to, testid }) => (
              <article className="fdn-support-card" key={title}>
                <div className="fdn-work-icon"><Icon size={26} /></div>
                <h3>{title}</h3>
                <p>{text}</p>
                {to ? (
                  <Link className="fdn-btn fdn-btn-dark" to={to} data-testid={testid}>
                    {cta} <ArrowRight size={18} />
                  </Link>
                ) : (
                  <a className="fdn-btn fdn-btn-outline" href={href} data-testid={testid}>
                    {cta} <ArrowRight size={18} />
                  </a>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* 10. Safeguards */}
      <section className="fdn-section" data-testid="foundation-safeguards-section">
        <div className="fdn-wrap">
          <span className="fdn-eyebrow">Safeguards</span>
          <h2 className="fdn-h2">How we keep seniors safe.</h2>
          <ul className="fdn-safe-list">
            {SAFEGUARDS.map(([head, body]) => (
              <li key={head}>
                <Lock className="fdn-safe-icon" size={20} />
                <span><strong>{head}</strong> {body}</span>
              </li>
            ))}
          </ul>
          <p className="fdn-lead" style={{ marginTop: '1.75rem' }}>
            To raise a concern, write to <a href={`mailto:${EMAIL}`} data-testid="foundation-concern-email">{EMAIL}</a>.
          </p>
        </div>
      </section>

      {/* 7 + 8. Trust, compliance and relationship */}
      <section className="fdn-section fdn-compliance" data-testid="foundation-compliance-section">
        <div className="fdn-wrap">
          <span className="fdn-eyebrow">Trust and compliance</span>
          <h2 className="fdn-h2">Registered, and accountable.</h2>
          <p className="fdn-lead">
            Elderra Foundation is an initiative of Giant-Tree Foundation, a not-for-profit company
            registered under Section 8 of the Companies Act, 2013.
          </p>
          <dl className="fdn-reg-table" data-testid="foundation-registration-table">
            {REGISTRATION.map(([k, v]) => (
              <div className="fdn-reg-row" key={k}>
                <dt>{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
          </dl>

          <div style={{ marginTop: '3.5rem' }}>
            <span className="fdn-eyebrow">Our relationship with Elderra</span>
            <h2 className="fdn-h2">Two organisations, one purpose.</h2>
            <p className="fdn-lead">
              Elderra is a for-profit technology startup that serves families in India&rsquo;s large
              cities. Giant-Tree Foundation is a separate not-for-profit company, set up in 2026 to
              bring Elderra&rsquo;s technology and services to older people who cannot afford them. The
              Foundation uses Elderra&rsquo;s technology on a non-profit basis. Donations and CSR funds
              received by the Foundation are used only for its not-for-profit work and never to pay
              for Elderra&rsquo;s commercial services.
            </p>
          </div>
        </div>
      </section>

      {/* 11. Team and contact */}
      <section className="fdn-section" data-testid="foundation-contact-section">
        <div className="fdn-wrap">
          <span className="fdn-eyebrow">Contact</span>
          <h2 className="fdn-h2">Talk to us.</h2>
          <div className="fdn-contact-grid">
            <div className="fdn-contact-card">
              <h4><Mail size={14} style={{ marginRight: 6, verticalAlign: '-2px' }} />Email</h4>
              <a href={`mailto:${EMAIL}`} data-testid="foundation-contact-email">{EMAIL}</a>
            </div>
            <div className="fdn-contact-card">
              <h4><Phone size={14} style={{ marginRight: 6, verticalAlign: '-2px' }} />Phone</h4>
              <a href={`tel:${PHONE}`} data-testid="foundation-contact-phone">89204-55730</a>
            </div>
            <div className="fdn-contact-card">
              <h4><MapPin size={14} style={{ marginRight: 6, verticalAlign: '-2px' }} />Address</h4>
              <p>H. No. 234, Third Floor, Right Side Mustafa Loge, Jamia Nagar, New Delhi 110025</p>
            </div>
          </div>
        </div>
      </section>

      <div className="fdn-wrap">
        <p className="fdn-note" data-testid="foundation-footer-note">
          Elderra Foundation is an initiative of Giant-Tree Foundation, a Section 8 not-for-profit
          company (CIN U88900DL2026NPL462782). &copy; 2026 Giant-Tree Foundation.
        </p>
      </div>
    </div>
  );
};

export default Foundation;
